import React, { useEffect, useState } from "react";
import {
  Drawer,
  Descriptions,
  Tag,
  Typography,
  Divider,
  List,
  Space,
  Button,
  Upload,
  Popconfirm,
  Spin,
  Empty,
  Timeline,
} from "antd";
import {
  PaperClipOutlined,
  DeleteOutlined,
  UploadOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  DOCUMENT_AND_IMAGE_ACCEPT,
  beforeUploadDocumentOrImage,
  filterDocumentOrImageFileList,
} from "../../../utils/fileUpload";

const { Title, Text } = Typography;

const STATUS_TAGS = {
  Draft: { color: "default" },
  "Pending HoD Review": { color: "orange" },
  "Pending Section Head Validation": { color: "orange" },
  "Pending Deputy Head Approval": { color: "orange" },
  Rejected: { color: "red" },
  Approved: { color: "green" },
  Unlocked: { color: "blue" },
};

const TYPE_LABELS = {
  scheme: "Scheme of Work",
  lesson_note: "Lesson Note",
  lesson_plan: "Lesson Plan",
  teaching_aid: "Teaching Aid",
  practical_guide: "Practical Guide",
};

const DETAIL_FALLBACK_FIELDS = [
  "description",
  "academicYear",
  "term",
  "subjectName",
  "className",
  "streamName",
  "teacherName",
  "created_at",
];

const hasValue = (value) =>
  value !== undefined && value !== null && value !== "";

const mergeMaterialDetail = (summary, fetched) => {
  if (!summary) return fetched;
  if (!fetched) return summary;

  const merged = { ...summary, ...fetched };

  DETAIL_FALLBACK_FIELDS.forEach((field) => {
    if (!hasValue(merged[field]) && hasValue(summary[field])) {
      merged[field] = summary[field];
    }
  });

  return merged;
};

const MaterialDetailDrawer = ({
  visible,
  onClose,
  materialId,
  material,
  onFetchDetail,
  onDeleteFile,
  onUploadFiles,
}) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [deletingFileId, setDeletingFileId] = useState(null);
  const [newFileList, setNewFileList] = useState([]);

  useEffect(() => {
    if (visible && materialId) {
      if (material) {
        setDetail(material);
      }
      loadDetail();
    }
    if (!visible) {
      setDetail(null);
      setNewFileList([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, materialId]);

  const loadDetail = async () => {
    setLoading(true);
    const data = await onFetchDetail(materialId);
    setDetail(mergeMaterialDetail(material, data));
    setLoading(false);
  };

  const handleDeleteFile = async (fileId) => {
    setDeletingFileId(fileId);
    const ok = await onDeleteFile(materialId, fileId);
    if (ok) {
      setDetail((prev) => ({
        ...prev,
        files: (prev?.files || []).filter((f) => f.id !== fileId),
      }));
    }
    setDeletingFileId(null);
  };

  const handleUploadMore = async () => {
    if (!newFileList.length) return;
    setUploadingFiles(true);
    const rawFiles = newFileList.map((f) => f.originFileObj || f);
    const uploaded = await onUploadFiles(materialId, rawFiles);
    if (uploaded) {
      setDetail((prev) => ({
        ...prev,
        files: [...(prev.files || []), ...uploaded],
      }));
      setNewFileList([]);
    }
    setUploadingFiles(false);
  };

  const uploadProps = {
    beforeUpload: beforeUploadDocumentOrImage,
    multiple: true,
    fileList: newFileList,
    onChange: ({ fileList }) =>
      setNewFileList(filterDocumentOrImageFileList(fileList)),
    accept: DOCUMENT_AND_IMAGE_ACCEPT,
  };

  const canManageFiles = detail?.status === "Draft";

  const renderContent = () => {
    if (loading && !detail)
      return (
        <Spin
          style={{ display: "block", textAlign: "center", marginTop: 40 }}
        />
      );
    if (!detail) return <Empty description="No details available" />;

    const statusTag = STATUS_TAGS[detail.status] || { color: "default" };
    const description =
      typeof detail.description === "string"
        ? detail.description.trim()
        : detail.description;

    return (
      <>
        <Descriptions column={1} bordered size="small">
          <Descriptions.Item label="Title">{detail.title}</Descriptions.Item>
          <Descriptions.Item label="Type">
            <Tag color="purple">{TYPE_LABELS[detail.type] || detail.type}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Academic Year">
            {detail.academicYear || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Term">
            {detail.term || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={statusTag.color}>{detail.status || "-"}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Delivery status">
            {detail.isDelivered ? (
              <Tag color="green">Delivered</Tag>
            ) : (
              <Tag color="default">Not delivered</Tag>
            )}
          </Descriptions.Item>
          {detail.isDelivered && (
            <Descriptions.Item label="Delivered At">
              {detail.deliveredAt
                ? dayjs(detail.deliveredAt).format("DD MMM YYYY HH:mm")
                : "-"}
            </Descriptions.Item>
          )}
          {detail.isDelivered && (
            <Descriptions.Item label="Delivered By">
              {detail.deliveredByName || "-"}
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Submission Count">
            {detail.submissionCount ?? 0}
          </Descriptions.Item>
          <Descriptions.Item label="Description">
            {description || "-"}
          </Descriptions.Item>
          {detail.submittedAt && (
            <Descriptions.Item label="Submitted On">
              {dayjs(detail.submittedAt).format("DD MMM YYYY HH:mm")}
            </Descriptions.Item>
          )}
          {detail.approvedAt && (
            <Descriptions.Item label="Approved At">
              {dayjs(detail.approvedAt).format("DD MMM YYYY HH:mm")}
            </Descriptions.Item>
          )}
        </Descriptions>

        <Divider orientation="left">Files</Divider>
        {detail.files?.length ? (
          <List
            size="small"
            dataSource={detail.files}
            renderItem={(file) => (
              <List.Item
                actions={[
                  <Button
                    type="text"
                    size="small"
                    icon={<DownloadOutlined />}
                    href={file.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  />,
                  canManageFiles && (
                    <Popconfirm
                      title="Remove this file?"
                      onConfirm={() => handleDeleteFile(file.id)}
                      okText="Remove"
                      okButtonProps={{ danger: true }}
                    >
                      <Button
                        type="text"
                        size="small"
                        danger
                        loading={deletingFileId === file.id}
                        icon={<DeleteOutlined />}
                      />
                    </Popconfirm>
                  ),
                ].filter(Boolean)}
              >
                <Space>
                  <PaperClipOutlined />
                  <Text>{file.filename}</Text>
                </Space>
              </List.Item>
            )}
          />
        ) : (
          <Text type="secondary">No files attached</Text>
        )}

        {canManageFiles && (
          <>
            <Divider orientation="left">Upload More Files</Divider>
            <Space direction="vertical" style={{ width: "100%" }}>
              <Upload {...uploadProps}>
                <Button icon={<UploadOutlined />}>Select Files</Button>
              </Upload>
              {newFileList.length > 0 && (
                <Button
                  type="primary"
                  loading={uploadingFiles}
                  onClick={handleUploadMore}
                >
                  Upload {newFileList.length} file
                  {newFileList.length > 1 ? "s" : ""}
                </Button>
              )}
            </Space>
          </>
        )}

        {detail.approvals?.length > 0 && (
          <>
            <Divider orientation="left">Approval History</Divider>
            <Timeline
              items={detail.approvals.map((a) => ({
                color: a.action === "Approved" ? "green" : "red",
                dot:
                  a.action === "Approved" ? (
                    <CheckCircleOutlined style={{ color: "#52c41a" }} />
                  ) : (
                    <CloseCircleOutlined style={{ color: "#ff4d4f" }} />
                  ),
                children: (
                  <div>
                    <Text strong>{a.stage}</Text>
                    <Tag
                      color={a.action === "Approved" ? "green" : "red"}
                      style={{ marginLeft: 8 }}
                    >
                      {a.action}
                    </Tag>
                    <div>
                      <Text type="secondary">{a.approverName}</Text>
                    </div>
                    {a.remarks && (
                      <div>
                        <Text italic>{a.remarks}</Text>
                      </div>
                    )}
                    <div>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {a.approvedAt
                          ? dayjs(a.approvedAt).format("DD MMM YYYY HH:mm")
                          : ""}
                      </Text>
                    </div>
                  </div>
                ),
              }))}
            />
          </>
        )}
      </>
    );
  };

  return (
    <Drawer
      title={
        <Title level={5} style={{ margin: 0 }}>
          Material Details
        </Title>
      }
      open={visible}
      onClose={onClose}
      width={650}
      destroyOnClose
    >
      {renderContent()}
    </Drawer>
  );
};

export default MaterialDetailDrawer;
